import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { SkeletonCards, ToastProvider } from '@serota/ui-web';
import { Login } from './auth/Login';
import { Impersonate } from './auth/Impersonate';
import { homeFor } from './auth/home';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { RequireAuth } from './components/Guard';
import { translateLegacyPath } from './lib/legacy-paths';
import { queryClient } from './lib/query';
import { SessionProvider, useSession } from './lib/session';
import { businessRoutes } from './portals/business/routes';
import { controlRoutes } from './portals/control/routes';

const SharedProposal = lazy(() =>
  import('./portals/share/SharedProposal').then((m) => ({ default: m.SharedProposal })),
);
const Gallery = lazy(() => import('./dev/gallery/Gallery').then((m) => ({ default: m.GalleryIndex })));
const GalleryPage = lazy(() => import('./dev/gallery/Gallery').then((m) => ({ default: m.GalleryPage })));
const ControlLayout = lazy(() => import('./portals/control/ControlLayout').then((m) => ({ default: m.ControlLayout })));
const BusinessLayout = lazy(() =>
  import('./portals/business/BusinessLayout').then((m) => ({ default: m.BusinessLayout })),
);

function Fallback() {
  return (
    <div className="sr-page">
      <SkeletonCards count={6} height={112} />
    </div>
  );
}

function Home() {
  const { status, context } = useSession();
  if (status === 'loading') return <Fallback />;
  if (status === 'signedOut' || !context) return <Navigate to="/giris" replace />;
  return <Navigate to={homeFor(context)} replace />;
}

/** Eski İngilizce adres (yer imi, SMS/e-posta bağlantısı) → Türkçe karşılığı; arama ve # korunur. Bilinmeyen adres → ana sayfa. */
function LegacyRedirect() {
  const loc = useLocation();
  const to = translateLegacyPath(loc.pathname);
  return <Navigate to={to ? { pathname: to, search: loc.search, hash: loc.hash } : '/'} replace />;
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Fallback />}>{children}</Suspense>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <BrowserRouter>
          <ToastProvider>
            <ImpersonationBanner />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/giris" element={<Login />} />
              <Route path="/destek-girisi" element={<Impersonate />} />
              <Route
                path="/paylasim/:token"
                element={
                  <Lazy>
                    <SharedProposal />
                  </Lazy>
                }
              />
              <Route
                path="/operasyon"
                element={
                  <RequireAuth>
                    <Lazy>
                      <ControlLayout />
                    </Lazy>
                  </RequireAuth>
                }
              >
                {controlRoutes}
              </Route>
              <Route
                path="/kurum"
                element={
                  <RequireAuth>
                    <Lazy>
                      <BusinessLayout />
                    </Lazy>
                  </RequireAuth>
                }
              >
                {businessRoutes}
              </Route>
              <Route
                path="/gelistirici/galeri"
                element={
                  <Lazy>
                    <Gallery />
                  </Lazy>
                }
              />
              <Route
                path="/gelistirici/galeri/:name"
                element={
                  <Lazy>
                    <GalleryPage />
                  </Lazy>
                }
              />
              <Route path="*" element={<LegacyRedirect />} />
            </Routes>
          </ToastProvider>
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  );
}
